import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { specialties, surgeries, treatmentSlug, contact } from "@/lib/healic-data";
import { hasTreatmentClinicalContent } from "@/lib/condition-slugs";
import healicLogo from "@/assets/healic-logo.webp.asset.json";

const navShortLabels: Record<string, string> = {
  "general-surgery": "General Surgery",
  gynaecology: "Gyne & Obs",
  "cosmetic-gynaecology": "Cosmetic Gyne",
  orthopaedics: "Orthopaedics",
  urology: "Urology",
  ent: "ENT",
  ophthalmology: "Ophthalmology",
};

const utilityLinks = [
  { to: "/doctors", label: "Doctors" },
  { to: "/locations", label: "Locations" },
  { to: "/surgeries", label: "Surgeries" },
  { to: "/assessment", label: "Do I Need Surgery?" },
  { to: "/run-with-healic", label: "Community Run" },
  { to: "/blog", label: "Blog" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
] as const;

function treatmentLinkProps(specialtySlug: string, t: string) {
  const slug = treatmentSlug(t);
  // Prefer dedicated treatment page when clinical content exists for this slug.
  if (hasTreatmentClinicalContent(slug)) {
    return { to: "/treatments/$slug" as const, params: { slug } };
  }
  const linked = surgeries.find(
    (s) =>
      s.specialtySlug === specialtySlug &&
      (s.name === t || s.name.toLowerCase().includes(t.toLowerCase().split(" ")[0])),
  );
  if (linked) {
    return { to: "/surgeries/$slug" as const, params: { slug: linked.slug } };
  }
  return { to: "/treatments/$slug" as const, params: { slug } };
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [mobileOpenSlug, setMobileOpenSlug] = useState<string | null>(null);
  return (
    <header className="sticky top-0 z-40 bg-primary text-primary-foreground shadow-soft">
      {/* Top row: logo + utility links + phone + CTA */}
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 lg:gap-4 xl:gap-6 lg:px-6 xl:px-8">
        <Link to="/" aria-label="Healic Multispeciality Hospital" className="flex items-center">
          <img
            src={healicLogo.url}
            alt="Healic Multispeciality Hospital"
            width={1920}
            height={570}
            fetchPriority="high"
            decoding="async"
            className="h-8 w-auto brightness-0 invert lg:h-9 xl:h-11"
          />
        </Link>

        <nav className="hidden items-center justify-center gap-0 lg:flex xl:gap-1 2xl:gap-1">
          <Link
            to="/"
            className="rounded-pill px-2 py-2 text-xs font-semibold tracking-wide opacity-85 transition-all hover:bg-white/10 hover:opacity-100 lg:px-2 lg:text-[11px] xl:px-2.5 xl:text-[13px] 2xl:px-4 2xl:text-sm"
            activeOptions={{ exact: true }}
            activeProps={{ className: "bg-accent text-accent-foreground opacity-100 shadow-cta" }}
          >
            Home
          </Link>
          {specialties.map((s) => (
            <div key={s.slug} className="group relative">
              <Link
                to="/specialties/$slug"
                params={{ slug: s.slug }}
                className="flex items-center justify-center gap-1 rounded-pill px-2 py-2 text-center text-xs font-semibold tracking-wide opacity-85 transition-all hover:bg-white/10 hover:opacity-100 lg:px-2 lg:text-[11px] xl:px-2.5 xl:text-[13px] 2xl:px-4 2xl:text-sm"
                activeProps={{
                  className: "bg-accent text-accent-foreground opacity-100 shadow-cta",
                }}
              >
                {navShortLabels[s.slug] ?? s.name}
                <ChevronDown className="h-3 w-3 transition-transform group-hover:rotate-180 2xl:h-3.5 2xl:w-3.5" />
              </Link>
              {/* Dropdown */}
              <div className="invisible absolute left-0 top-full z-50 pt-2 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100">
                <div className="min-w-[220px] overflow-hidden rounded-xl bg-primary shadow-card ring-1 ring-white/10">
                  <Link
                    to="/specialties/$slug"
                    params={{ slug: s.slug }}
                    className="block border-b border-white/10 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-accent hover:bg-white/5"
                  >
                    {s.name} overview
                  </Link>
                  <ul className="py-1">
                    {s.treatments.map((t) => {
                      const props = treatmentLinkProps(s.slug, t);
                      return (
                        <li key={t}>
                          <Link
                            {...props}
                            className="block px-4 py-2 text-sm font-medium text-primary-foreground/90 hover:bg-accent hover:text-accent-foreground"
                          >
                            {t}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex xl:gap-3">
          <a
            href={`tel:${contact.phoneTel}`}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-pill bg-white/5 px-2 py-2 text-xs font-medium ring-1 ring-white/15 hover:bg-white/10 lg:px-2 lg:text-[11px] xl:px-3 xl:text-[13px] 2xl:px-4 2xl:text-sm"
          >
            <Phone className="h-3 w-3 text-accent 2xl:h-4 2xl:w-4" /> {contact.phone}
          </a>
        </div>

        <button
          aria-label="Toggle menu"
          className="rounded-md p-2 lg:hidden"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-white/10 bg-primary lg:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-semibold hover:bg-white/5"
            >
              Home
            </Link>
            <p className="mt-2 px-3 pt-2 text-[10px] font-semibold uppercase tracking-widest text-accent">
              Specialties
            </p>
            {specialties.map((s) => {
              const isOpen = mobileOpenSlug === s.slug;
              return (
                <div key={s.slug} className="flex flex-col">
                  <div className="flex items-center justify-between">
                    <Link
                      to="/specialties/$slug"
                      params={{ slug: s.slug }}
                      onClick={() => setOpen(false)}
                      className="flex-1 rounded-md px-3 py-2 text-sm font-medium hover:bg-white/5"
                    >
                      {navShortLabels[s.slug] ?? s.name}
                    </Link>
                    <button
                      aria-label="Toggle treatments"
                      className="rounded-md p-2 hover:bg-white/5"
                      onClick={() => setMobileOpenSlug(isOpen ? null : s.slug)}
                    >
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                  </div>
                  {isOpen && (
                    <ul className="mb-1 ml-3 border-l border-white/10 pl-3">
                      {s.treatments.map((t) => {
                        const props = treatmentLinkProps(s.slug, t);
                        return (
                          <li key={t}>
                            <Link
                              {...props}
                              onClick={() => setOpen(false)}
                              className="block rounded-md px-3 py-2 text-sm text-primary-foreground/85 hover:bg-white/5"
                            >
                              {t}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
            <p className="mt-3 px-3 pt-2 text-[10px] font-semibold uppercase tracking-widest text-accent">
              Explore
            </p>
            {utilityLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-sm font-medium hover:bg-white/5"
              >
                {l.label}
              </Link>
            ))}
            <div className="mt-3 grid grid-cols-2 gap-2 px-1">
              <a
                href={`tel:${contact.phoneTel}`}
                className="rounded-pill bg-white/10 px-4 py-2 text-center text-sm"
              >
                Call now
              </a>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="rounded-pill bg-accent px-4 py-2 text-center text-sm font-semibold text-accent-foreground"
              >
                Book
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
